import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Button, message, Space, Tag, Typography, DatePicker, Select, Table, Modal, Image, Empty } from 'antd';
import { ReloadOutlined, ClockCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { apiBangChamCong } from '../services/authService';
import { getTotalWorkHours } from '../utils/attendance';
import dayjs from 'dayjs';

const { Text } = Typography;

const LOAI_CHAM_CONG_OPTIONS = [
    { value: 'tat_ca', label: 'Tất cả' },
    { value: 'tai_cong_ty', label: 'Tại công ty' },
    { value: 'ngoai_cong_ty', label: 'Ngoài công ty' },
];

const getLoaiChamCongLabel = (loai) => {
    if (loai === 'tai_cong_ty') return 'Tại công ty';
    if (loai === 'ngoai_cong_ty') return 'Ngoài công ty';
    return 'Không xác định';
};

const getLoaiChamCongColor = (loai) => {
    if (loai === 'tai_cong_ty') return 'green';
    if (loai === 'ngoai_cong_ty') return 'orange';
    return 'default';
};

// Tên trường ngày/nhân viên/loại chấm công/ảnh/toạ độ trả về từ backend chưa được xác nhận
// nên thử lần lượt các tên trường thường gặp trong dự án để tránh vỡ giao diện
const getRecordDate = (record) => {
    const raw = record.ngay || record.ngay_cham_cong || record.ngayChamCong || record.date
        || record.thoiGianVao || record.createdAt;
    return raw ? dayjs(raw).format('YYYY-MM-DD') : null;
};

const getRecordUserId = (record) => record.user_id ?? record.userId ?? record.nhan_vien_id ?? record.id;

const getRecordUserName = (record) => record.username || record.name || record.ten_nhan_vien
    || record.tenNhanVien || `NV #${getRecordUserId(record)}`;

const getRecordLoai = (record) => record.loai_cham_cong || record.loaiChamCong || null;

const getImageVao = (record) => record.image_vao || record.anh_vao || record.imageVao || record.image_in || record.image || null;

const getImageRa = (record) => record.image_ra || record.anh_ra || record.imageRa || record.image_out || null;

const getToaDoVao = (record) => record.toa_do_vao || record.toaDoVao || record.toa_do || null;

const getToaDoRa = (record) => record.toa_do_ra || record.toaDoRa || null;

const parseToaDo = (toaDo) => {
    if (!toaDo) return null;
    let lat;
    let lng;
    if (Array.isArray(toaDo) && toaDo.length === 2) {
        [lat, lng] = toaDo;
    } else if (typeof toaDo === 'object' && toaDo.lat !== undefined && toaDo.lng !== undefined) {
        lat = toaDo.lat;
        lng = toaDo.lng;
    } else if (typeof toaDo === 'string') {
        const parts = toaDo.split(',').map(part => part.trim());
        if (parts.length === 2) {
            [lat, lng] = parts;
        }
    }
    lat = Number(lat);
    lng = Number(lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
};

const BangChamCongComponent = () => {
    const [loading, setLoading] = useState(false);
    const [thangNam, setThangNam] = useState(dayjs());
    const [loaiChamCong, setLoaiChamCong] = useState('tat_ca');
    const [rawRecords, setRawRecords] = useState([]);
    const [modalDetail, setModalDetail] = useState(null);
    const mapInstances = useRef({});

    const fetchBangChamCong = useCallback(async () => {
        if (!thangNam) return;
        setLoading(true);
        try {
            const thang = thangNam.month() + 1;
            const nam = thangNam.year();
            const response = await apiBangChamCong('', thang, nam);

            console.log('=== BANG CHAM CONG RESPONSE ===');
            console.log('Raw response:', response);

            let records = [];
            if (response && Array.isArray(response.data)) {
                records = response.data;
            } else if (Array.isArray(response)) {
                records = response;
            }

            console.log('Số bản ghi chấm công:', records.length);
            if (records.length > 0) {
                console.log('Cấu trúc bản ghi đầu tiên:', records[0]);
            }
            console.log('================================');

            setRawRecords(records);
            message.success(`Đã tải ${records.length} bản ghi chấm công tháng ${thang}/${nam}`);
        } catch (error) {
            console.log('=== BANG CHAM CONG ERROR ===');
            console.log('Error:', error);
            message.error('Không thể tải bảng chấm công');
        } finally {
            setLoading(false);
        }
    }, [thangNam]);

    useEffect(() => {
        fetchBangChamCong();
    }, [fetchBangChamCong]);

    const filteredRecords = useMemo(() => {
        if (loaiChamCong === 'tat_ca') return rawRecords;
        return rawRecords.filter(record => getRecordLoai(record) === loaiChamCong);
    }, [rawRecords, loaiChamCong]);

    const employees = useMemo(() => {
        const map = new Map();
        filteredRecords.forEach(record => {
            const id = getRecordUserId(record);
            if (id === undefined || id === null) return;
            if (!map.has(id)) {
                map.set(id, { id, name: getRecordUserName(record) });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'vi'));
    }, [filteredRecords]);

    const cellMap = useMemo(() => {
        const map = new Map();
        filteredRecords.forEach(record => {
            const ngay = getRecordDate(record);
            const userId = getRecordUserId(record);
            if (!ngay || userId === undefined || userId === null) return;
            const key = `${ngay}__${userId}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(record);
        });
        return map;
    }, [filteredRecords]);

    const dates = useMemo(() => {
        const set = new Set();
        filteredRecords.forEach(record => {
            const ngay = getRecordDate(record);
            if (ngay) set.add(ngay);
        });
        return Array.from(set).sort((a, b) => b.localeCompare(a));
    }, [filteredRecords]);

    const openModal = (ngay, employee, records) => {
        setModalDetail({ ngay, employee, records });
    };

    const renderCell = useCallback((ngay, employee) => {
        const records = cellMap.get(`${ngay}__${employee.id}`) || [];
        if (records.length === 0) {
            return <Text type="secondary">-</Text>;
        }

        return (
            <div
                onClick={() => openModal(ngay, employee, records)}
                style={{ cursor: 'pointer', padding: '2px 0' }}
            >
                {records.map((record, index) => {
                    const gioVao = record.thoiGianVao ? dayjs(record.thoiGianVao).format('HH:mm') : '-';
                    const gioRa = record.thoiGianRa ? dayjs(record.thoiGianRa).format('HH:mm') : '-';
                    const loai = getRecordLoai(record);
                    return (
                        <div
                            key={index}
                            style={{
                                marginBottom: index < records.length - 1 ? 8 : 0,
                                paddingBottom: index < records.length - 1 ? 6 : 0,
                                borderBottom: index < records.length - 1 ? '1px dashed #e8e8e8' : 'none',
                            }}
                        >
                            <div>
                                <Text style={{ fontSize: '12px' }}>
                                    <ClockCircleOutlined style={{ color: '#0D9488' }} /> {gioVao} - {gioRa}
                                </Text>
                            </div>
                            <div style={{ marginTop: 2 }}>
                                <Tag color={getLoaiChamCongColor(loai)} style={{ fontSize: '10px', lineHeight: '16px' }}>
                                    {getLoaiChamCongLabel(loai)}
                                </Tag>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }, [cellMap]);

    const columns = useMemo(() => {
        const dateColumn = {
            title: 'Ngày',
            dataIndex: 'ngay',
            key: 'ngay',
            fixed: 'left',
            width: 110,
            render: (ngay) => (
                <Text strong>{dayjs(ngay).format('DD/MM/YYYY')}</Text>
            ),
        };

        const employeeColumns = employees.map(employee => ({
            title: employee.name,
            key: String(employee.id),
            width: 170,
            render: (_, row) => renderCell(row.ngay, employee),
        }));

        return [dateColumn, ...employeeColumns];
    }, [employees, renderCell]);

    const dataSource = dates.map(ngay => ({ key: ngay, ngay }));

    // Bản đồ vào/ra cho từng bản ghi trong modal đang mở, giữ nguyên index của
    // modalDetail.records (null khi bản ghi đó không có toạ độ hợp lệ) để id map
    // và ảnh khớp đúng bản ghi khi render.
    const mapTargets = useMemo(() => {
        if (!modalDetail) return [];
        return modalDetail.records.map((record, index) => {
            const vao = parseToaDo(getToaDoVao(record));
            const ra = parseToaDo(getToaDoRa(record));
            return {
                vao: vao ? { id: `bcc-leaflet-map-vao-${index}`, ...vao } : null,
                ra: ra ? { id: `bcc-leaflet-map-ra-${index}`, ...ra } : null,
            };
        });
    }, [modalDetail]);

    const showMapError = (container, lat, lng) => {
        container.innerHTML = `
            <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;background-color:#f5f5f5;border-radius:6px;padding:16px;text-align:center;">
                <div style="font-size:32px;margin-bottom:8px;">🗺️</div>
                <div style="font-size:13px;color:#999;">Không thể tải bản đồ</div>
                <div style="font-size:11px;color:#ccc;margin-top:4px;">Tọa độ: ${lat}, ${lng}</div>
            </div>
        `;
    };

    const loadLeafletMap = useCallback((containerId, lat, lng) => {
        try {
            if (typeof window !== 'undefined' && window.L) {
                const mapContainer = document.getElementById(containerId);
                if (!mapContainer) return;

                // Khi đổi bản ghi mà không đóng modal, React tái sử dụng lại cùng
                // 1 DOM node cho cùng containerId. Nếu không gỡ map cũ bằng chính
                // API của Leaflet trước, node vẫn còn cờ khởi tạo và L.map() sẽ
                // ném lỗi "Map container is already initialized" — đây là lý do
                // bản đồ lúc hiển thị được, lúc không.
                if (mapInstances.current[containerId]) {
                    mapInstances.current[containerId].remove();
                    delete mapInstances.current[containerId];
                }
                mapContainer.innerHTML = '';

                try {
                    const map = window.L.map(containerId).setView([lat, lng], 16);
                    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                        attribution: '© OpenStreetMap contributors',
                    }).addTo(map);
                    window.L.marker([lat, lng]).addTo(map).bindPopup('Vị trí chấm công').openPopup();
                    mapInstances.current[containerId] = map;
                    // Modal vừa mở/đổi kích thước có thể khiến Leaflet đo sai kích
                    // thước container lúc khởi tạo, làm bản đồ hiển thị lệch/xám.
                    setTimeout(() => map.invalidateSize(), 150);
                } catch (mapError) {
                    console.error('Error creating Leaflet map:', mapError);
                    showMapError(mapContainer, lat, lng);
                }
            } else {
                loadLeafletFromCDN(containerId, lat, lng);
            }
        } catch (error) {
            console.error('Error in loadLeafletMap:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadLeafletFromCDN = useCallback((containerId, lat, lng) => {
        try {
            const existingCSS = document.querySelector('link[href*="leaflet.css"]');
            if (!existingCSS) {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
                link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
                link.crossOrigin = '';
                document.head.appendChild(link);
            }

            const existingScript = document.querySelector('script[src*="leaflet.js"]');
            if (!existingScript) {
                const script = document.createElement('script');
                script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
                script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
                script.crossOrigin = '';
                script.onload = () => setTimeout(() => loadLeafletMap(containerId, lat, lng), 200);
                script.onerror = () => {
                    const mapContainer = document.getElementById(containerId);
                    if (mapContainer) showMapError(mapContainer, lat, lng);
                };
                document.head.appendChild(script);
            } else {
                setTimeout(() => loadLeafletMap(containerId, lat, lng), 200);
            }
        } catch (error) {
            console.error('Error loading Leaflet from CDN:', error);
        }
    }, [loadLeafletMap]);

    useEffect(() => {
        if (!modalDetail) return undefined;
        const timer = setTimeout(() => {
            mapTargets.forEach(({ vao, ra }) => {
                if (vao) loadLeafletMap(vao.id, vao.lat, vao.lng);
                if (ra) loadLeafletMap(ra.id, ra.lat, ra.lng);
            });
        }, 300);
        return () => clearTimeout(timer);
    }, [modalDetail, mapTargets, loadLeafletMap]);

    useEffect(() => {
        if (modalDetail) return;
        Object.values(mapInstances.current).forEach(map => map.remove());
        mapInstances.current = {};
    }, [modalDetail]);

    const renderImageMapRow = (label, imageSrc, mapTarget) => (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 320px' }}>
                <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginBottom: 4 }}>
                    {label}
                </Text>
                {imageSrc ? (
                    <Image
                        src={imageSrc}
                        width={320}
                        height={360}
                        style={{ objectFit: 'cover', borderRadius: 6 }}
                    />
                ) : (
                    <div
                        style={{
                            width: 320,
                            height: 360,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f0f0f0',
                            borderRadius: 6,
                        }}
                    >
                        <Text type="secondary">Không có ảnh</Text>
                    </div>
                )}
            </div>
            <div style={{ flex: '1 1 320px', minWidth: 320 }}>
                <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginBottom: 4 }}>
                    Vị trí {label.toLowerCase()}
                </Text>
                {mapTarget ? (
                    <div
                        id={mapTarget.id}
                        style={{
                            height: '360px',
                            width: '100%',
                            border: '1px solid #d9d9d9',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                        }}
                    >
                        <Text type="secondary" style={{ fontSize: '12px' }}>Đang tải bản đồ...</Text>
                    </div>
                ) : (
                    <div
                        style={{
                            height: '360px',
                            width: '100%',
                            border: '1px dashed #d9d9d9',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fafafa',
                        }}
                    >
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <EnvironmentOutlined /> Không có vị trí
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Card title="Bảng chấm công theo tháng">
            <style>
                {`
                    .bcc-row-even { background-color: #ffffff; }
                    .bcc-row-odd { background-color: #f0fdfa; }
                    .bcc-row-even:hover > td, .bcc-row-odd:hover > td {
                        background-color: #ccfbf1 !important;
                    }
                `}
            </style>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
                <Space size="large" wrap>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Tháng:</span>
                        <DatePicker
                            picker="month"
                            value={thangNam}
                            onChange={(date) => date && setThangNam(date)}
                            format="MM/YYYY"
                            placeholder="Chọn tháng"
                            allowClear={false}
                            style={{ width: '130px' }}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Loại chấm công:</span>
                        <Select
                            value={loaiChamCong}
                            onChange={setLoaiChamCong}
                            options={LOAI_CHAM_CONG_OPTIONS}
                            style={{ width: '160px' }}
                        />
                    </div>
                </Space>

                <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={fetchBangChamCong}
                    loading={loading}
                >
                    Tải lại
                </Button>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Text>Đang tải dữ liệu...</Text>
                </div>
            ) : (
                <>
                    <div style={{ marginBottom: '12px', textAlign: 'right' }}>
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                            {employees.length} nhân viên • {dates.length} ngày có điểm danh
                        </Text>
                    </div>

                    {dataSource.length === 0 ? (
                        <Empty description="Không có dữ liệu chấm công trong tháng này" />
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={dataSource}
                            pagination={false}
                            bordered
                            size="small"
                            scroll={{ x: 'max-content', y: 600 }}
                            rowClassName={(_, index) => (index % 2 === 0 ? 'bcc-row-even' : 'bcc-row-odd')}
                        />
                    )}
                </>
            )}

            <Modal
                title={modalDetail ? `Chi tiết chấm công - ${modalDetail.employee.name} - ${dayjs(modalDetail.ngay).format('DD/MM/YYYY')}` : ''}
                open={!!modalDetail}
                onCancel={() => setModalDetail(null)}
                footer={null}
                width="90%"
                style={{ maxWidth: '1100px', top: 12 }}
                styles={{ body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', paddingRight: 8 } }}
            >
                {modalDetail && (
                    <Image.PreviewGroup>
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            {modalDetail.records.map((record, index) => {
                                const gioVao = record.thoiGianVao ? dayjs(record.thoiGianVao).format('HH:mm') : '-';
                                const gioRa = record.thoiGianRa ? dayjs(record.thoiGianRa).format('HH:mm') : '-';
                                const loai = getRecordLoai(record);
                                const tongGio = getTotalWorkHours(record.thoiGianVao, record.thoiGianRa);
                                const imageVao = getImageVao(record);
                                const imageRa = getImageRa(record);
                                const mapTarget = mapTargets[index] || { vao: null, ra: null };

                                return (
                                    <Card key={index} size="small" style={{ background: '#fafafa' }}>
                                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                            <Space wrap>
                                                <Text><ClockCircleOutlined /> Vào: <Text strong>{gioVao}</Text></Text>
                                                <Text>Ra: <Text strong>{gioRa}</Text></Text>
                                                {tongGio !== null && (
                                                    <Text>
                                                        Tổng giờ làm: <Text strong style={{ color: '#52c41a' }}>{tongGio.toFixed(1)}h</Text>
                                                    </Text>
                                                )}
                                                <Tag color={getLoaiChamCongColor(loai)}>{getLoaiChamCongLabel(loai)}</Tag>
                                            </Space>

                                            {renderImageMapRow('Ảnh vào', imageVao, mapTarget.vao)}
                                            {record.thoiGianRa && renderImageMapRow('Ảnh ra', imageRa, mapTarget.ra)}
                                        </Space>
                                    </Card>
                                );
                            })}
                        </Space>
                    </Image.PreviewGroup>
                )}
            </Modal>
        </Card>
    );
};

export default BangChamCongComponent;
